'use client';

import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DragHandle as TiptapDragHandle } from '@tiptap/extension-drag-handle-react';
import type { Editor } from '@tiptap/react';
import type { Node as PMNode } from '@tiptap/pm/model';

import { useUiEditorState } from 'containers/Tiptap/hooks/use-ui-editor-state';
import { GripIcon } from 'containers/Tiptap/components/tiptap-icons/grip-icon';
import { PlusIcon } from 'containers/Tiptap/components/tiptap-icons/plus-icon';
import {
  hasProgrammaticSlash,
  markProgrammaticSlash,
} from 'containers/Tiptap/components/tiptap-ui-utils/suggestion-menu/programmatic-slash';
import { RENDERED_BAND_SELECTOR } from 'containers/Tiptap/lib/header-footer-dom';

import 'containers/Tiptap/components/tiptap-ui/drag-handle/drag-handle.scss';

export interface DragHandleProps {
  editor: Editor | null;
}

export function DragHandle({ editor }: DragHandleProps): ReactElement | null {
  const [isVisible, setIsVisible] = useState(false);
  const [isOverBand, setIsOverBand] = useState(false);
  const { lockDragHandle } = useUiEditorState(editor);
  const hoveredBlockRef = useRef<{ node: PMNode; pos: number } | null>(null);

  useEffect(() => {
    if (!editor) return undefined;

    const handleSelectionUpdate = (): void => {
      const { selection } = editor.state;
      const isEmptyTextBlock =
        selection.$anchor.parent.isTextblock && selection.$anchor.parent.content.size === 0;

      setIsVisible(!isEmptyTextBlock);
    };

    editor.on('selectionUpdate', handleSelectionUpdate);
    editor.on('update', handleSelectionUpdate);

    return (): void => {
      editor.off('selectionUpdate', handleSelectionUpdate);
      editor.off('update', handleSelectionUpdate);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor) return undefined;

    const dom = editor.view.dom;
    const handleMouseOver = (event: MouseEvent): void => {
      setIsOverBand(Boolean((event.target as HTMLElement | null)?.closest(RENDERED_BAND_SELECTOR)));
    };

    dom.addEventListener('mouseover', handleMouseOver);
    return (): void => dom.removeEventListener('mouseover', handleMouseOver);
  }, [editor]);

  const handleNodeChange = useCallback(({ node, pos }: { node: PMNode | null; pos: number }): void => {
    hoveredBlockRef.current = node && pos >= 0 ? { node, pos } : null;
    if (node) {
      setIsVisible(true);
    }
  }, []);

  const getReferencedVirtualElement = useCallback(() => {
    const target = hoveredBlockRef.current;
    const dom = target && editor?.view.nodeDOM(target.pos);
    if (!(dom instanceof HTMLElement)) return null;

    // The first block's box starts under the floated page header, so align to its first line instead.
    const range = document.createRange();
    range.selectNodeContents(dom);
    const firstLine = range.getClientRects()[0];

    return {
      getBoundingClientRect: (): DOMRect => {
        const block = dom.getBoundingClientRect();
        if (!firstLine || firstLine.top <= block.top) return block;
        return new DOMRect(block.x, firstLine.top, block.width, block.height - (firstLine.top - block.top));
      },
    };
  }, [editor]);

  if (!editor || lockDragHandle) {
    return null;
  }

  const handleAddBlock = (): void => {
    // Ignore further clicks while a previously-inserted '/' is still pending
    // (i.e. the suggestion menu from a prior click is still open).
    if (hasProgrammaticSlash(editor)) {
      return;
    }

    const target = hoveredBlockRef.current;
    if (!target) return;
    const { node, pos } = target;

    // Only insert inline if the block is empty AND not a code block:
    //   - allowedPrefixes is [' '], so '/' must follow a paragraph boundary (or space)
    //     to fire. Inserting at the end of "Hello world" would give "Hello world/" and
    //     the menu would never open, leaving the programmatic-slash mark stuck.
    //   - code blocks (`code: true`) accept the slash as code text; transforming a code
    //     block via the slash menu is not the desired UX.
    if (node.isTextblock && node.content.size === 0 && !node.type.spec.code) {
      const slashPos = pos + 1;
      editor.chain().focus().setTextSelection(slashPos).insertContent('/').run();
      markProgrammaticSlash(editor, { kind: 'inline', from: slashPos, to: slashPos + 1 });
      return;
    }

    // Non-empty block (or non-textblock): insert a new paragraph after with '/' at start
    // so the Suggestion plugin fires reliably.
    const insertPos = pos + node.nodeSize;
    editor
      .chain()
      .focus()
      .insertContentAt(insertPos, { type: 'paragraph', content: [{ type: 'text', text: '/' }] })
      .setTextSelection(insertPos + 2)
      .run();
    markProgrammaticSlash(editor, { kind: 'paragraph', from: insertPos });
  };

  return (
    <TiptapDragHandle
      editor={editor}
      onNodeChange={handleNodeChange}
      getReferencedVirtualElement={getReferencedVirtualElement}
    >
      <div className="drag-handle-container" data-visible={isVisible} data-over-band={isOverBand}>
        <button
          type="button"
          className="drag-handle-button drag-handle-add"
          onClick={handleAddBlock}
          aria-label="Add block"
        >
          <PlusIcon className="drag-handle-icon" />
        </button>
        <button type="button" className="drag-handle-button drag-handle-grip" aria-label="Drag to move">
          <GripIcon className="drag-handle-icon" />
        </button>
      </div>
    </TiptapDragHandle>
  );
}

export default DragHandle;
